import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { useLocation } from 'react-router';
import { Spinner, Typography, useOverlayContext } from 'seagull';
import { redirect, routes } from '~/constants';
import { useIoContext } from '~/contexts';
import { LoginMethod, useSendLoginGtmEvents } from '~/hooks/useSendLoginGtmEvents';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import XeroLogo from '~/integrations/assets/XeroLogo';
import { useStoreActions } from '~/store/store-context';
import styles from './XeroSignInButton.module.scss';
export const XeroSignInButtonTestIds = pathgen('XeroSignInButtonTestIds');
export default function XeroSignInButton(props) {
    const { location, mode, ensureEmail } = props;
    const getUtms = useUtmsParams();
    const { api } = useStoreActions();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: xeroTexts } = useLocaleKeys('pages.auth.xero');
    const { pathname, search } = useLocation();
    const { showErrorToast } = useOverlayContext();
    const { changeLocation } = useIoContext();
    const [loading, setLoading] = useState(false);
    const { sendGtmLoginSuccessEvent, sendGtmLoginFailEvent } = useSendLoginGtmEvents();
    const returnTo = useMemo(()=>redirect.getReturnToFromSearch(search), [
        search
    ]);
    const iconSize = 24;
    async function handleClick() {
        setLoading(true);
        const utms = getUtms();
        const redirectUrl = returnTo ? "".concat(location.origin).concat(redirect.getPathAndSearch({
            returnTo
        })) : "".concat(location.origin).concat(routes.home);
        const request = mode === 'signUp' ? api.auth.xeroSignUp({
            redirectUrl,
            utms
        }) : api.auth.xeroSignIn({
            redirectUrl,
            utms,
            ensureEmail: ensureEmail ? {
                email: ensureEmail,
                errorUrl: "".concat(location.origin).concat(pathname).concat(routes.loginError)
            } : undefined
        });
        const { success, data } = await handleRequest(request);
        setLoading(false);
        if (success) {
            if (mode === 'signIn') {
                await sendGtmLoginSuccessEvent(LoginMethod.Xero);
            }
            changeLocation(data);
        } else {
            if (mode === 'signIn') {
                sendGtmLoginFailEvent(LoginMethod.Xero);
            }
            showErrorToast(commonTexts.toasts.failedToContinueWithXero(), XeroSignInButtonTestIds.ErrorToast());
        }
    }
    return /*#__PURE__*/ _jsx("div", {
        className: styles.root,
        children: /*#__PURE__*/ _jsxs("button", {
            className: styles.button,
            "data-testid": XeroSignInButtonTestIds.Button(),
            disabled: loading,
            onClick: handleClick,
            children: [
                loading ? /*#__PURE__*/ _jsx(Spinner, {
                    size: iconSize,
                    testId: XeroSignInButtonTestIds.Loading()
                }) : /*#__PURE__*/ _jsx(XeroLogo, {
                    height: iconSize
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    skin: loading ? 'disabled' : 'standard',
                    children: xeroTexts.continueWithXero()
                })
            ]
        })
    });
}
